// src/components/Journals.jsx
"use client"

import React, { useState, useEffect } from "react"
import {
  Row,
  Col,
  Button,
  Card,
  Form,
  InputGroup,
  DropdownButton,
  Dropdown,
  Table,
  Pagination,
  Alert,
  Spinner,
} from "react-bootstrap"
import axios from "axios"
import {
  exportToExcel,
  exportToPDF,
  exportToCSV,
} from "./utils/exportUtils"

function Journals({ user, payrollId, group }) {
  const API_BASE = "http://109.75.164.217:84/api"

  // state
  const [journalData, setJournalData] = useState([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [journalCreated, setJournalCreated] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 10

  // columns for export
  const headers = [
    "project",
    "accountingString",
    "totalDebitPerAccountingString",
    "totalDebitPerProject",
  ]

  // create or fetch journal
  const fetchJournal = async () => {
    if (!payrollId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await axios.post(`${API_BASE}/Journal/${payrollId}`)
      setJournalData(data)
      setJournalCreated(true)
      setCurrentPage(1)
    } catch (err) {
      if (err.response?.status === 404) {
        setJournalData([])
        setJournalCreated(false)
      } else {
        setError(err.response?.data || err.message)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!payrollId) {
      setError("Please select a payroll first")
      return
    }
    setCreating(true)
    setError(null)
    try {
      await axios.post(`${API_BASE}/Journal/${payrollId}`)
      await fetchJournal()
    } catch (err) {
      setError(err.response?.data || err.message)
    } finally {
      setCreating(false)
    }
  }

  useEffect(() => {
    fetchJournal()
  }, [payrollId])

  // filter, sort & paginate
  const filteredData = journalData
    .filter(r =>
      `${r.project} ${r.accountingString}`.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) =>
      a.accountingString.localeCompare(b.accountingString)
    )

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage))
  const paginated = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  // build pagination items
  const paginationItems = []
  for (let num = 1; num <= totalPages; num++) {
    if (
      num === 1 ||
      num === totalPages ||
      (num >= currentPage - 1 && num <= currentPage + 1)
    ) {
      paginationItems.push(
        <Pagination.Item
          key={num}
          active={num === currentPage}
          onClick={() => setCurrentPage(num)}
        >
          {num}
        </Pagination.Item>
      )
    } else if (
      (num === currentPage - 2 && currentPage > 3) ||
      (num === currentPage + 2 && currentPage < totalPages - 2)
    ) {
      paginationItems.push(<Pagination.Ellipsis key={`ell-${num}`} />)
    }
  }

  // prepare export rows: only last-of-group or single record shows project total
  const exportRows = filteredData.map(row => {
    const groupRows = filteredData.filter(
      r => r.accountingString === row.accountingString
    )
    const isLast = groupRows.length === 1 || groupRows[groupRows.length - 1] === row
    return {
      project: row.project,
      accountingString: row.accountingString,
      totalDebitPerAccountingString: row.totalDebitPerAccountingString,
      totalDebitPerProject: isLast ? row.totalDebitPerProject : "",
    }
  })

  return (
    <Card className="shadow-sm mb-4 border-0">
      <Card.Body className="pt-0">
        {!payrollId ? (
          <Alert variant="warning">
            Please first <strong>Search & Process</strong> a payroll.
          </Alert>
        ) : (
          <>
            {/* Create / Refresh */}
            <Row className="mb-4">
              <Col className="d-flex gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handleCreate}
                  disabled={creating || loading}
                >
                  {creating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Creating…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-plus-circle me-2" />
                      Create Journal
                    </>
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={fetchJournal}
                  disabled={creating || loading}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-2" />
                      Refresh
                    </>
                  )}
                </Button>
              </Col>
            </Row>

            {/* Error */}
            {error && (
              <Alert variant="danger" className="mb-4">
                <i className="bi bi-exclamation-triangle-fill me-2" />
                {error}
              </Alert>
            )}

            {/* Loading / Empty */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-3">Loading journal data…</p>
              </div>
            ) : journalData.length === 0 ? (
              <Alert variant="info">
                {journalCreated
                  ? "No journal entries found."
                  : "No journal has been created yet. Click “Create Journal” above."}
              </Alert>
            ) : (
              <>
                {/* Search + Export */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <InputGroup style={{ maxWidth: 300 }}>
                    <InputGroup.Text>
                      <i className="bi bi-search" />
                    </InputGroup.Text>
                    <Form.Control
                      size="sm"
                      placeholder="Search Project / Accounting…"
                      value={searchQuery}
                      onChange={e => {
                        setSearchQuery(e.target.value)
                        setCurrentPage(1)
                      }}
                    />
                  </InputGroup>

                  <DropdownButton
                    size="sm"
                    variant="outline-primary"
                    title={
                      <>
                        <i className="bi bi-download me-1" />
                        Export
                      </>
                    }
                    drop="up"
                    align="end"
                  >
                    <Dropdown.Item
                      onClick={() =>
                        exportToExcel({
                          title: "Journal",
                          id: payrollId,
                          rows: exportRows,
                          headers,
                        })
                      }
                    >
                      <i className="bi bi-file-earmark-excel me-2" /> Excel
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        exportToCSV({
                          title: "Journal",
                          id: payrollId,
                          rows: exportRows,
                          headers,
                        })
                      }
                    >
                      <i className="bi bi-file-earmark-spreadsheet me-2" /> CSV
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() =>
                        exportToPDF({
                          title: "Journal",
                          id: payrollId,
                          rows: exportRows,
                          headers,
                        })
                      }
                    >
                      <i className="bi bi-file-earmark-pdf me-2" /> PDF
                    </Dropdown.Item>
                  </DropdownButton>
                </div>

                {/* Table */}
                <div
                  className="border rounded"
                  style={{ overflowY: "auto", maxHeight: "60vh" }}
                >
                  <Table striped hover responsive className="mb-0">
                    <thead className="bg-white sticky-top">
                      <tr>
                        <th className="text-start">Project</th>
                        <th className="text-start">Accounting String</th>
                        <th className="text-end">Total Debit Per Accounting</th>
                        <th className="text-end">Total Debit Per Project</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((row, idx) => {
                        const groupRows = filteredData.filter(
                          r => r.accountingString === row.accountingString
                        )
                        const showProjectTotal =
                          groupRows.length === 1 ||
                          groupRows[groupRows.length - 1] === row

                        return (
                          <tr
                            key={`${row.project}-${row.accountingString}-${idx}`}
                          >
                            <td className="text-start">{row.project}</td>
                            <td className="text-start">
                              {row.accountingString}
                            </td>
                            <td className="text-end">
                              {row.totalDebitPerAccountingString.toLocaleString()}
                            </td>
                            <td className="text-end">
                              {showProjectTotal
                                ? row.totalDebitPerProject.toLocaleString()
                                : ""}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>

                {/* Pagination */}
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="small text-muted">
                    Showing {paginated.length} of {filteredData.length} entries
                  </div>
                  <Pagination>
                    <Pagination.Prev
                      onClick={() =>
                        setCurrentPage(prev => Math.max(1, prev - 1))
                      }
                      disabled={currentPage === 1}
                    />
                    {paginationItems}
                    <Pagination.Next
                      onClick={() =>
                        setCurrentPage(prev => Math.min(totalPages, prev + 1))
                      }
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              </>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  )
}

export default Journals
